import { useList } from '@refinedev/core';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { queryKeys } from '@/repositories/queryKeys';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

const usePackageItemsList = (packageItems, setPackageItems, packageId, isEditing: boolean) => {
  const { packageItemRepository } = useRepositoriesContext();
  const {
    result: packageItemsResult,
    query: { isLoading, isFetching, refetch, isFetchedAfterMount },
  } = useList<PackageItem>({
    resource: 'packages_items',
    pagination: { pageSize: 1000 },
    meta: {
      package: packageId,
    },
    queryOptions: {
      queryKey: queryKeys.packageItems.byPackage(packageId),
      enabled: packageId !== undefined,
      staleTime: 0,
    },
  });
  const { mutateAsync: saveItems } = useMutation({
    mutationFn: async () => {
      if (packageId != undefined) {
        await packageItemRepository.savePackageItems(packageItems, packageId);
      }
    },
    onSuccess: async () => {
      const { data } = await refetch();

      if (data) {
        setPackageItems(data.data);
      }
    },
  });

  useEffect(() => {
    if (isFetchedAfterMount && packageItemsResult && !isEditing) {
      setPackageItems(packageItemsResult.data);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageItemsResult, isFetchedAfterMount]);

  const handleDelete = useCallback(
    record => {
      const itemFounded = packageItems.find(item =>
        record.id ? item.id == record.id : item.name == record.name,
      );

      if (!itemFounded) return;

      const itemDeleted: PackageItem = { ...itemFounded, toDelete: true };
      const newPackageItems = packageItems.map(item =>
        item.id
          ? item.id === itemDeleted.id
            ? itemDeleted
            : item
          : item.name === itemDeleted.name
            ? itemDeleted
            : item,
      );

      setPackageItems(newPackageItems);
    },
    [packageItems, setPackageItems],
  );

  return { isLoading, isFetching, refetch, handleDelete, saveItems };
};

export default usePackageItemsList;
